import {
	Box,
	Flex,
	Grid,
	GridItem,
	Spinner,
	Text,
} from '@chakra-ui/react';
import type React from 'react';
import FaceliftBadge from '@/components/Atoms/FaceliftBadge';
import { FaceliftScrollContainer } from '@/components/Atoms/FaceliftScrollContainer';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';

interface DetailField {
	label: string;
	value: string | number | undefined | null | string[] | React.ReactNode;
	link?: string; // Optional: External URL to make value clickable (opens in new tab)
	linkText?: string; // Optional: Custom text for the link (if different from value)
	navigateTo?: string; // Optional: Internal route to navigate to (uses React Router)
}

interface DetailColumn {
	data: DetailField[];
}

interface AppCrashDetailsViewProps {
	data: DetailColumn[];
	isLoading?: boolean;
	gridConfig?: {
		base?: string;
		md?: string;
		lg?: string;
		xl?: string;
	};
	gap?: {
		base?: number;
		md?: number;
		lg?: number;
		xl?: number;
	};
	dialogWidth?: string;
}

const DetailField = ({
	label,
	value,
}: DetailField) => {
	const isReactNode = typeof value !== 'string' && typeof value !== 'number' && !Array.isArray(value) && value !== null && value !== undefined;
	
	return (
		<Box display="flex" justifyContent="space-between" gap={2} alignItems="flex-start">
			<FaceliftText fontSize="sm" fontWeight="bold" flexShrink={0}>
				{label}
			</FaceliftText>
			{isReactNode ? (
				<Box flex={1} minWidth={0}>
					{value}
				</Box>
			) : (
				<FaceliftText
					fontSize="sm"
					fontWeight={400}
					textAlign="right"
					wordBreak="break-word"
					overflowWrap="break-word"
					minWidth={0}
					flex={1}
				>
					{value?.toString() || '-'}
				</FaceliftText>
			)}
		</Box>
	);
};

const ArrayDetailField = ({ label, value }: DetailField) => (
	<Box display="flex" flexDirection="column" gap={2} width="100%">
		<Text fontSize="sm" fontWeight="bold">
			{label}
		</Text>
		<Flex flexWrap="wrap" gap={2}>
			{Array.isArray(value) && value.length > 0 ? (
				value.map((item) => (
					// <Badge key={`${label}-${item}`}>{item}</Badge>
					<FaceliftBadge
						key={`${label}-${item}`}
						status={item}
						badgeColor="brand.solid"
					/>
				))
			) : (
				<Text fontSize="sm" fontWeight={400}>
					-
				</Text>
			)}
		</Flex>
	</Box>
);

const AppCrashDetailsView: React.FC<AppCrashDetailsViewProps> = ({
	data,
	isLoading = false,
	gridConfig = {
		base: '1fr',
		md: 'repeat(3, 1fr)',
	},
	gap = {
		base: 3,
		md: 10,
	},
}) => {
	const scalarColumns: DetailColumn[] = [];
	const arrayFields: DetailField[] = [];

	data.forEach((column) => {
		const scalarFields: DetailField[] = [];

		column.data.forEach((field) => {
			if (Array.isArray(field.value)) {
				arrayFields.push(field);
			} else {
				scalarFields.push(field);
			}
		});

		if (scalarFields.length > 0) {
			scalarColumns.push({ data: scalarFields });
		}
	});

	return (
		<FaceliftScrollContainer overflowY="auto" minWidth="fit-content">
			<Show when={isLoading}>
				<Flex justify="center" align="center" minHeight="300px" width="100%">
					<Spinner size="xl" colorPalette="brand" />
				</Flex>
			</Show>
			<Show when={data && data.length > 0 && !isLoading}>
				<Box display="flex" flexDirection="column" gap={6}>
					<Show when={scalarColumns.length > 0}>
						<Grid
							templateColumns={{
								base: gridConfig.base || '1fr',
								md: gridConfig.md || 'repeat(3, 1fr)',
								lg: gridConfig.lg,
								xl: gridConfig.xl,
							}}
							gap={{
								base: gap.base || 3,
								md: gap.md || 10,
								lg: gap.lg,
								xl: gap.xl,
							}}
						>
							{scalarColumns.map((column) => (
								<GridItem
									key={`column-${column.data[0]?.label || Math.random()}`}
									minWidth={0}
								>
									<Grid templateColumns="1fr" gap={{ base: 3, md: 6 }}>
										{column.data.map((field) => (
											<GridItem key={field.label} minWidth={0}>
												<DetailField
													label={field.label}
													value={field.value}
												/>
											</GridItem>
										))}
									</Grid>
								</GridItem>
							))}
						</Grid>
					</Show>

					<Show when={arrayFields.length > 0}>
						<Grid templateColumns="1fr" gap={4}>
							{arrayFields.map((field) => (
								<GridItem key={`array-${field.label}`}>
									<ArrayDetailField label={field.label} value={field.value} />
								</GridItem>
							))}
						</Grid>
					</Show>
				</Box>
			</Show>

			<Show when={(!data || data.length === 0) && !isLoading}>
				<Box textAlign="center" py={8}>
					<Text fontSize="md" color="gray.500">
						No data found
					</Text>
				</Box>
			</Show>
		</FaceliftScrollContainer>
	);
};

export default AppCrashDetailsView;
